import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { HistoryPoint } from '../intelligence';
import { chartFont, chrome } from '../reportTheme';
import ChartTooltip from './ChartTooltip';
import { dayLabel, dayTs, timeAxis } from './timeAxis';

const axisTick = { fill: chrome.muted, fontSize: 12, fontFamily: chartFont };

export interface LineSeries {
  name: string;
  points: HistoryPoint[];
  color: string;
  dashed?: boolean;
}

interface MultiLineChartProps {
  series: LineSeries[];
  unit?: string;
  max?: number;
}

/**
 * Several KPI histories on one time axis - plan against actual, operative
 * against quality-assured progress, the five management scores. Days a series
 * has no value leave a gap in that line only; every measured day carries a dot,
 * and the time axis covers at least a week.
 */
const MultiLineChart: React.FC<MultiLineChartProps> = ({ series, unit = '%', max = 100 }) => {
  const rows = useMemo(() => {
    const byDay = new Map<number, Record<string, number>>();
    for (const line of series) {
      for (const point of line.points) {
        const ts = dayTs(point.date);
        const row = byDay.get(ts) ?? { ts };
        row[line.name] = Math.round(point.value * 10) / 10;
        byDay.set(ts, row);
      }
    }
    return [...byDay.values()].sort((a, b) => a.ts - b.ts);
  }, [series]);
  const axis = useMemo(() => timeAxis(rows.map((row) => row.ts)), [rows]);

  const fmt = (value: number | string) => `${value}${unit}`;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={chrome.grid} vertical={false} />
        <XAxis
          dataKey="ts"
          type="number"
          scale="time"
          domain={axis.domain}
          ticks={axis.ticks}
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: chrome.axis }}
          tickFormatter={dayLabel}
        />
        <YAxis
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          width={44}
          domain={[0, max]}
          tickFormatter={fmt}
        />
        <Tooltip
          content={<ChartTooltip valueFormatter={fmt} />}
          labelFormatter={(value) => dayLabel(Number(value))}
          cursor={{ stroke: chrome.axis, strokeWidth: 1 }}
        />
        <Legend
          iconType="plainline"
          wrapperStyle={{ fontFamily: chartFont, fontSize: 12, color: chrome.textSecondary }}
        />
        {series.map((line) => (
          <Line
            key={line.name}
            type="monotone"
            dataKey={line.name}
            stroke={line.color}
            strokeWidth={2}
            strokeDasharray={line.dashed ? '5 4' : undefined}
            dot={{ r: 3, fill: line.color, strokeWidth: 0 }}
            connectNulls={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: chrome.surface }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default MultiLineChart;
